import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

type SwapBudgetDistributorParams =
  components['schemas']['SwapDistributorRoleForDistributorGroupEndpoint_SwapDistributorRoleForDistributorGroupRequestBody'];
type SwapBudgetDistributorResponse =
  components['schemas']['SwapDistributorRoleForDistributorGroupEndpoint_SwapDistributorRoleForDistributorGroupResponse'];

interface SwapDistributorRoleMutationParams {
  organizationId: number;
  params: SwapBudgetDistributorParams;
}

export function useSwapDistributorRoleForDistributorGroup() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ organizationId, params }: SwapDistributorRoleMutationParams): Promise<SwapBudgetDistributorResponse> => {
      const client = createApiClient(getAccessToken);
      const { data } = await client.POST('/api/organizations/{organizationId}/distributor-groups/swap-distributor', {
        params: {
          path: { organizationId },
        },
        body: {
          userId: params.userId,
          currentDistributorGroupId: params.currentDistributorGroupId,
          newDistributorGroupId: params.newDistributorGroupId,
        },
      });

      return data as SwapBudgetDistributorResponse;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['usersByDistributorGroup'] }),
        queryClient.invalidateQueries({ queryKey: ['distributorGroupByUser'], exact: false }),
      ]);
    },
    onError: async (error: Error) => {
      throw error;
    },
  });

  return {
    swapDistributorRole: mutation.mutateAsync,
    isSwappingRole: mutation.isPending,
  };
}
